/**
 * Image.js — modificado
 * Cambios: nueva prop `rawStyle` que preserva estilos del HTML importado.
 */
import React from 'react';
import { useNode } from '@craftjs/core';
import { TextField, FormControl, FormLabel, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  settings: {
    padding: theme.spacing(2),
  },
  rawStyleTextarea: {
    width: '100%',
    fontFamily: '"Roboto Mono", monospace',
    fontSize: '0.68rem',
    padding: theme.spacing(1),
    border: '1px solid #ccc',
    borderRadius: 4,
    minHeight: 80,
    resize: 'vertical',
    outline: 'none',
    lineHeight: 1.5,
    color: '#333',
    backgroundColor: '#fafafa',
  },
  hint: {
    fontSize: '0.72rem',
    color: '#999',
    marginTop: 4,
    marginBottom: theme.spacing(1),
  },
}));

export const Image = ({ src, alt, width, height, objectFit, rawStyle }) => {
  const classes = useStyles();
  const { connectors: { connect, drag } } = useNode();

  const style = {
    ...rawStyle,
    width:     width     ? `${width}px`     : (rawStyle?.width  || 'auto'),
    height:    height    ? `${height}px`    : (rawStyle?.height || 'auto'),
    maxWidth:  '100%',
    objectFit: objectFit || rawStyle?.objectFit || 'cover',
    cursor:    'move',
  };

  return (
    <img
      ref={(ref) => connect(drag(ref))}
      src={src}
      alt={alt}
      style={style}
    />
  );
};

const ImageSettings = () => {
  const classes = useStyles();
  const {
    actions: { setProp },
    src,
    alt,
    width,
    height,
    objectFit,
    rawStyle,
  } = useNode((node) => ({
    src:       node.data.props.src,
    alt:       node.data.props.alt,
    width:     node.data.props.width,
    height:    node.data.props.height,
    objectFit: node.data.props.objectFit,
    rawStyle:  node.data.props.rawStyle,
  }));

  const rawStyleStr = rawStyle ? JSON.stringify(rawStyle, null, 2) : '{}';

  const handleRawStyleChange = (val) => {
    try {
      const parsed = JSON.parse(val);
      setProp((props) => (props.rawStyle = parsed));
    } catch (_) {}
  };

  return (
    <div className={classes.settings}>

      <FormControl fullWidth margin="normal">
        <FormLabel>URL de imagen</FormLabel>
        <TextField
          value={src || ''}
          onChange={(e) => setProp((props) => (props.src = e.target.value))}
          placeholder="https://ejemplo.com/imagen.jpg"
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Texto alternativo (alt)</FormLabel>
        <TextField
          value={alt || ''}
          onChange={(e) => setProp((props) => (props.alt = e.target.value))}
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Ancho (px)</FormLabel>
        <TextField
          type="number"
          value={width || ''}
          onChange={(e) => setProp((props) => (props.width = e.target.value))}
          placeholder="Automático"
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Alto (px)</FormLabel>
        <TextField
          type="number"
          value={height || ''}
          onChange={(e) => setProp((props) => (props.height = e.target.value))}
          placeholder="Automático"
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Ajuste de imagen</FormLabel>
        <TextField
          select
          value={objectFit || 'cover'}
          onChange={(e) => setProp((props) => (props.objectFit = e.target.value))}
          SelectProps={{ native: true }}
        >
          <option value="cover">Cubrir (cover)</option>
          <option value="contain">Contener (contain)</option>
          <option value="fill">Rellenar (fill)</option>
          <option value="none">Sin ajuste</option>
          <option value="scale-down">Reducir si es necesario</option>
        </TextField>
      </FormControl>

      {rawStyle && Object.keys(rawStyle).length > 0 && (
        <FormControl fullWidth margin="normal">
          <FormLabel>Estilos heredados (CSS)</FormLabel>
          <Typography className={classes.hint}>
            Capturados del HTML original.
          </Typography>
          <textarea
            className={classes.rawStyleTextarea}
            defaultValue={rawStyleStr}
            onBlur={(e) => handleRawStyleChange(e.target.value)}
            spellCheck={false}
            style={{ marginTop: 8 }}
          />
        </FormControl>
      )}
    </div>
  );
};

Image.craft = {
  props: {
    src:       'https://via.placeholder.com/400x300',
    alt:       'Imagen de ejemplo',
    width:     400,
    height:    300,
    objectFit: 'cover',
    rawStyle:  {},
  },
  related: {
    settings: ImageSettings,
  },
};
